// src/components/EmojiPicker.tsx
// Emoji selector with categorized grid and search.

import { useState, useMemo, useRef, useEffect } from 'react';
import { cn } from '@core/ui';
import { AnchoredMenu } from './AnchoredMenu';

// ---------------------------------------------------------------------------
// Emoji data — curated set of commonly used project/work emojis
// ---------------------------------------------------------------------------

interface EmojiCategory {
  id: string;
  icon: string;
  emojis: string[];
}

const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    id: 'work',
    icon: '💼',
    emojis: [
      '📋', '📊', '📈', '📉', '📌', '📎', '📁', '📂',
      '🗂️', '📝', '📄', '📑', '🗒️', '🗓️', '📅', '📆',
      '💼', '🏢', '🏗️', '🏭', '🧰', '🔧', '🔨', '⚙️',
      '🔩', '🛠️', '⚡', '🔌', '💡', '🔋', '📡', '🖥️',
    ],
  },
  {
    id: 'dev',
    icon: '💻',
    emojis: [
      '💻', '🖥️', '⌨️', '🖱️', '💾', '💿', '📀', '🔮',
      '🧪', '🔬', '🧬', '🧩', '🎮', '🕹️', '📱', '📲',
      '🌐', '🔗', '🛡️', '🔒', '🔓', '🔑', '🗝️', '🐛',
      '🐞', '🤖', '👾', '🧠', '📟', '📠', '🖨️', '🔍',
    ],
  },
  {
    id: 'objects',
    icon: '🚀',
    emojis: [
      '🚀', '✈️', '🚁', '🛸', '🏎️', '🚂', '⛵', '🎯',
      '🏆', '🥇', '🏅', '🎖️', '🎪', '🎭', '🎨', '🖼️',
      '🎬', '🎵', '🎶', '🎤', '📷', '📸', '🔭', '🧲',
      '💎', '👑', '🎁', '🎈', '🎉', '🎊', '🪄', '✨',
    ],
  },
  {
    id: 'nature',
    icon: '🌿',
    emojis: [
      '🌟', '⭐', '🌙', '☀️', '🌈', '🔥', '💧', '❄️',
      '🌊', '🌍', '🌎', '🌏', '🌸', '🌺', '🌻', '🌹',
      '🌿', '🍀', '🌴', '🌲', '🍁', '🍂', '🌾', '🌵',
      '🦋', '🐝', '🦊', '🐺', '🦅', '🐉', '🦄', '🐬',
    ],
  },
  {
    id: 'symbols',
    icon: '💠',
    emojis: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
      '💯', '✅', '❌', '⚠️', '🚫', '♻️', '🔴', '🟠',
      '🟡', '🟢', '🔵', '🟣', '⚪', '⚫', '🟤', '🔶',
      '🔷', '🔸', '🔹', '💠', '🏳️', '🏴', '🚩', '🏁',
    ],
  },
  {
    id: 'food',
    icon: '☕',
    emojis: [
      '☕', '🍵', '🧋', '🥤', '🍺', '🍷', '🍕', '🍔',
      '🌮', '🍣', '🍰', '🧁', '🍩', '🍪', '🍫', '🍬',
      '🍎', '🍊', '🍋', '🍇', '🍓', '🍑', '🥑', '🌶️',
      '🧀', '🥐', '🥖', '🥨', '🥞', '🧇', '🍿', '🥜',
    ],
  },
];

const ALL_EMOJIS = EMOJI_CATEGORIES.flatMap(c => c.emojis);

// Keyword tags per emoji so search can actually match — emoji glyphs carry no
// text of their own. Keep entries short; first occurrence wins for emojis
// that appear in more than one category (e.g. 🖥️ in both "work" and "dev").
const EMOJI_KEYWORDS: Record<string, string> = {
  '📋': 'clipboard checklist', '📊': 'chart bar graph stats', '📈': 'chart trending up growth',
  '📉': 'chart trending down decline', '📌': 'pin pushpin', '📎': 'paperclip attach clip',
  '📁': 'folder', '📂': 'folder open', '🗂️': 'card index dividers organize',
  '📝': 'memo note write pencil', '📄': 'page document file', '📑': 'bookmark tabs pages',
  '🗒️': 'spiral notepad note', '🗓️': 'spiral calendar date', '📅': 'calendar date schedule',
  '📆': 'calendar tear-off date', '💼': 'briefcase work business job',
  '🏢': 'office building company', '🏗️': 'construction crane building',
  '🏭': 'factory industry plant', '🧰': 'toolbox tools kit', '🔧': 'wrench tool fix repair',
  '🔨': 'hammer tool build', '⚙️': 'gear settings config', '🔩': 'nut bolt hardware',
  '🛠️': 'tools hammer wrench build fix', '⚡': 'lightning bolt zap fast power',
  '🔌': 'plug electric power', '💡': 'bulb idea light', '🔋': 'battery power energy',
  '📡': 'satellite antenna signal', '🖥️': 'desktop computer monitor screen',
  '💻': 'laptop computer code dev', '⌨️': 'keyboard type', '🖱️': 'mouse computer click',
  '💾': 'save disk floppy', '💿': 'cd disk media', '📀': 'dvd disk media',
  '🔮': 'crystal ball magic predict', '🧪': 'test tube science lab',
  '🔬': 'microscope science research', '🧬': 'dna science genetics',
  '🧩': 'puzzle piece integration plugin', '🎮': 'game controller gaming',
  '🕹️': 'joystick game arcade', '📱': 'phone mobile app', '📲': 'phone incoming call mobile',
  '🌐': 'globe web internet network', '🔗': 'link chain url', '🛡️': 'shield security protect',
  '🔒': 'lock secure locked', '🔓': 'unlock open unlocked', '🔑': 'key access password',
  '🗝️': 'old key vintage access', '🐛': 'bug insect issue error', '🐞': 'ladybug bug insect',
  '🤖': 'robot bot ai automation', '👾': 'alien monster game invader', '🧠': 'brain mind ai smart',
  '📟': 'pager beeper', '📠': 'fax machine', '🖨️': 'printer print', '🔍': 'search magnify find',
  '🚀': 'rocket launch ship fast', '✈️': 'plane airplane travel flight',
  '🚁': 'helicopter chopper', '🛸': 'ufo saucer alien', '🏎️': 'race car speed fast',
  '🚂': 'train locomotive', '⛵': 'sailboat boat sail', '🎯': 'target goal dart aim',
  '🏆': 'trophy award win champion', '🥇': 'medal gold win first', '🏅': 'medal sports award',
  '🎖️': 'medal military honor', '🎪': 'circus tent event', '🎭': 'theater masks drama',
  '🎨': 'palette art paint design', '🖼️': 'picture frame art gallery',
  '🎬': 'clapper film movie video', '🎵': 'music note song', '🎶': 'music notes song',
  '🎤': 'microphone mic sing', '📷': 'camera photo picture', '📸': 'camera flash photo snap',
  '🔭': 'telescope space astronomy', '🧲': 'magnet attract', '💎': 'gem diamond jewel value',
  '👑': 'crown royal king queen', '🎁': 'gift present box', '🎈': 'balloon party celebrate',
  '🎉': 'party popper celebrate confetti', '🎊': 'confetti ball celebrate party',
  '🪄': 'magic wand spell', '✨': 'sparkles shiny new magic',
  '🌟': 'star glowing shine special', '⭐': 'star favorite', '🌙': 'moon crescent night',
  '☀️': 'sun sunny day bright', '🌈': 'rainbow color', '🔥': 'fire flame hot trending',
  '💧': 'droplet water drop', '❄️': 'snowflake cold winter', '🌊': 'wave ocean water sea',
  '🌍': 'earth globe world europe africa', '🌎': 'earth globe world americas',
  '🌏': 'earth globe world asia australia', '🌸': 'blossom flower cherry',
  '🌺': 'hibiscus flower tropical', '🌻': 'sunflower flower', '🌹': 'rose flower love',
  '🌿': 'herb leaf plant nature', '🍀': 'clover luck four-leaf', '🌴': 'palm tree tropical',
  '🌲': 'evergreen tree pine', '🍁': 'maple leaf autumn fall', '🍂': 'fallen leaf autumn fall',
  '🌾': 'sheaf wheat grain', '🌵': 'cactus desert plant', '🦋': 'butterfly insect',
  '🐝': 'bee insect honey', '🦊': 'fox animal', '🐺': 'wolf animal',
  '🦅': 'eagle bird animal', '🐉': 'dragon mythical', '🦄': 'unicorn mythical magic',
  '🐬': 'dolphin animal sea', '❤️': 'heart red love', '🧡': 'heart orange love',
  '💛': 'heart yellow love', '💚': 'heart green love', '💙': 'heart blue love',
  '💜': 'heart purple love', '🖤': 'heart black love', '🤍': 'heart white love',
  '💯': 'hundred points perfect done', '✅': 'check mark done complete success',
  '❌': 'cross mark no error fail', '⚠️': 'warning alert caution',
  '🚫': 'prohibited no forbidden', '♻️': 'recycle reuse green', '🔴': 'circle red dot status',
  '🟠': 'circle orange dot status', '🟡': 'circle yellow dot status', '🟢': 'circle green dot status active',
  '🔵': 'circle blue dot status', '🟣': 'circle purple dot status', '⚪': 'circle white dot status',
  '⚫': 'circle black dot status', '🟤': 'circle brown dot status', '🔶': 'diamond orange shape',
  '🔷': 'diamond blue shape', '🔸': 'diamond small orange shape', '🔹': 'diamond small blue shape',
  '💠': 'diamond dot shape gem', '🏳️': 'flag white surrender', '🏴': 'flag black',
  '🚩': 'flag triangle marker', '🏁': 'flag checkered race finish',
  '☕': 'coffee cup drink hot', '🍵': 'tea cup drink hot', '🧋': 'bubble tea drink',
  '🥤': 'drink cup soda', '🍺': 'beer mug drink', '🍷': 'wine glass drink',
  '🍕': 'pizza food slice', '🍔': 'burger food hamburger', '🌮': 'taco food mexican',
  '🍣': 'sushi food japanese', '🍰': 'cake slice dessert', '🧁': 'cupcake dessert',
  '🍩': 'donut dessert', '🍪': 'cookie dessert', '🍫': 'chocolate dessert candy',
  '🍬': 'candy sweet', '🍎': 'apple fruit', '🍊': 'orange fruit', '🍋': 'lemon fruit citrus',
  '🍇': 'grapes fruit', '🍓': 'strawberry fruit', '🍑': 'peach fruit',
  '🥑': 'avocado fruit healthy', '🌶️': 'pepper spicy hot chili', '🧀': 'cheese food dairy',
  '🥐': 'croissant bread pastry', '🥖': 'baguette bread', '🥨': 'pretzel bread snack',
  '🥞': 'pancakes food breakfast', '🧇': 'waffle food breakfast', '🍿': 'popcorn snack movie',
  '🥜': 'peanuts nuts snack',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface EmojiPickerProps {
  value: string;
  onChange: (emoji: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  noResultsLabel?: string;
  clearLabel?: string;
  className?: string;
}

export function EmojiPicker({ value, onChange, placeholder, searchPlaceholder, noResultsLabel, clearLabel, className }: EmojiPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(EMOJI_CATEGORIES[0].id);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);


  // Outside click, Escape and repositioning live in AnchoredMenu.

  // Focus search on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Real keyword search — matches emoji keyword tags and category ids.
  const filteredEmojis = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return ALL_EMOJIS.filter((emoji, i, arr) => arr.indexOf(emoji) === i).filter((emoji) => {
      const keywords = EMOJI_KEYWORDS[emoji] ?? '';
      return keywords.includes(q);
    });
  }, [search]);

  const handleSelect = (emoji: string) => {
    onChange(emoji);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setOpen(false);
  };

  return (
    <div ref={ref} className={cn('relative', className)}>
      {/* Trigger button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={placeholder}
        className={cn(
          'flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          open && 'ring-2 ring-ring',
        )}
      >
        {value ? (
          <span className="text-xl leading-none">{value}</span>
        ) : (
          <span className="text-muted-foreground">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
        )}
        <svg className="h-3.5 w-3.5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown via portal */}
      <AnchoredMenu
        anchor={buttonRef}
        ignoreRef={ref}
        open={open}
        onClose={() => { setOpen(false); setSearch(''); }}
        role="none"
        width={320}
        maxHeight={340}
        className="overflow-hidden"
      >
        {/* Search + clear */}
        <div className="flex items-center gap-2 border-b border-border p-2">
          <div className="relative flex-1">
            <svg className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded border border-border bg-background py-1.5 pl-7 pr-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="rounded px-2 py-1.5 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
              title={clearLabel}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Category tabs */}
        {!search.trim() && (
          <div className="flex gap-0.5 border-b border-border px-2 py-1">
            {EMOJI_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategory(cat.id)}
                className={cn(
                  'rounded px-2 py-1 text-base transition-colors',
                  activeCategory === cat.id
                    ? 'bg-accent'
                    : 'hover:bg-accent/50',
                )}
                title={cat.id}
              >
                {cat.icon}
              </button>
            ))}
          </div>
        )}

        {/* Emoji grid */}
        <div className="overflow-y-auto p-2" style={{ maxHeight: 208 }}>
          {filteredEmojis?.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">{noResultsLabel}</p>
          ) : (
            <div className="grid grid-cols-8 gap-0.5">
              {(filteredEmojis || EMOJI_CATEGORIES.find(c => c.id === activeCategory)?.emojis || []).map((emoji, i) => (
                <button
                  key={`${emoji}-${i}`}
                  type="button"
                  onClick={() => handleSelect(emoji)}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded text-lg transition-colors',
                    'hover:bg-accent',
                    value === emoji && 'bg-accent ring-1 ring-primary',
                  )}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      </AnchoredMenu>
    </div>
  );
}
